import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from './Icons.tsx';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-teal-700 text-white hover:bg-teal-800 disabled:bg-teal-700/50',
  secondary:
    'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 dark:border-navy-500 dark:bg-navy-800 dark:text-slate-100 dark:hover:bg-navy-700',
  ghost: 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-navy-800',
  danger: 'bg-red-700 text-white hover:bg-red-800 disabled:bg-red-700/50',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md';
  icon?: IconName;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, busy, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed',
        size === 'sm' ? 'px-2.5 py-1.5 text-sm' : 'px-4 py-2 text-sm',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner small /> : icon ? <Icon name={icon} className="h-4 w-4" /> : null}
      {children}
    </button>
  );
});

/** Icon-only button: always has an accessible label (also shown as a tooltip). */
export function IconButton({
  icon,
  label,
  className,
  ...rest
}: Omit<ButtonProps, 'children' | 'icon'> & { icon: IconName; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-navy-800',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} className="h-5 w-5" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

const inputCls =
  'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500/30 disabled:bg-slate-100 dark:border-navy-500 dark:bg-navy-900 dark:text-slate-100 dark:disabled:bg-navy-800';

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
  optional?: boolean;
  /** Use a fixed id for the control (e.g. so an error summary can link to it). */
  id?: string;
}

/** Label + control + hint + error, wired together for screen readers. */
export function Field({ label, hint, error, children, className, optional, id: fixedId }: FieldProps) {
  const autoId = useId();
  const id = fixedId ?? autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('space-y-1', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800 dark:text-slate-200">
        {label}
        {optional && <span className="ms-1 font-normal text-slate-500">(optional)</span>}
      </label>
      {children({ id, describedBy, invalid: !!error })}
      {hint && (
        <p id={hintId} className="text-xs text-slate-600 dark:text-slate-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className="flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-400">
          <Icon name="alert" className="h-3.5 w-3.5" /> {error}
        </p>
      )}
    </div>
  );
}

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function TextInput({ className, invalid, ...rest }, ref) {
  return (
    <input
      ref={ref}
      dir="auto"
      aria-invalid={invalid || undefined}
      className={cx(inputCls, invalid && 'border-red-600', className)}
      {...rest}
    />
  );
});

export function TextArea({
  className,
  invalid,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      dir="auto"
      aria-invalid={invalid || undefined}
      className={cx(inputCls, invalid && 'border-red-600', className)}
      {...rest}
    />
  );
}

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ className, invalid, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(inputCls, 'pe-8', invalid && 'border-red-600', className)}
      {...rest}
    >
      {children}
    </select>
  );
});

export function Checkbox({
  label,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-400 text-teal-700 accent-teal-700"
        {...rest}
      />
      <label htmlFor={id} className="text-sm">
        <span className="font-medium text-slate-800 dark:text-slate-200">{label}</span>
        {hint && <span className="block text-xs text-slate-600 dark:text-slate-400">{hint}</span>}
      </label>
    </div>
  );
}

/** On/off switch with a visible label and explanation. */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={id} className="text-sm font-medium text-slate-800 dark:text-slate-200">
          {label}
        </label>
        {hint && <p className="text-xs text-slate-600 dark:text-slate-400">{hint}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
          checked ? 'bg-teal-700' : 'bg-slate-300 dark:bg-navy-500',
        )}
      >
        <span
          className={cx(
            'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5 rtl:-translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Spinner({ small, label = 'Loading' }: { small?: boolean; label?: string }) {
  return (
    <span role="status" className="inline-flex items-center">
      <svg
        className={cx('animate-spin text-current', small ? 'h-4 w-4' : 'h-6 w-6')}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 p-8 text-slate-600 dark:text-slate-300">
      <Spinner /> {label}
    </div>
  );
}

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';
const TONES: Record<Tone, string> = {
  info: 'border-navy-200 bg-navy-50 text-navy-900 dark:border-navy-500 dark:bg-navy-800 dark:text-navy-50',
  success:
    'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-700 dark:bg-teal-800/30 dark:text-teal-100',
  warning:
    'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100',
  danger: 'border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-900/30 dark:text-red-100',
  neutral:
    'border-slate-200 bg-slate-50 text-slate-800 dark:border-navy-700 dark:bg-navy-900 dark:text-slate-200',
};
const TONE_ICON: Record<Tone, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  danger: 'alert',
  neutral: 'info',
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('flex gap-3 rounded-lg border p-3 text-sm', TONES[tone], className)}
    >
      <Icon name={TONE_ICON[tone]} className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

/** Error summary at the top of a form: focused on appear, links jump to fields. */
export function ErrorSummary({
  title = 'Please fix the following',
  errors,
}: {
  title?: string;
  errors: Array<{ path: string; message: string; href?: string }>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const key = errors.map((e) => e.message).join('|');
  useEffect(() => {
    if (key) ref.current?.focus();
  }, [key]);
  if (!errors.length) return null;
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      aria-labelledby="error-summary-title"
      className="rounded-lg border-2 border-red-600 bg-red-50 p-4 dark:bg-red-900/30"
    >
      <h2 id="error-summary-title" className="font-semibold text-red-900 dark:text-red-100">
        {title}
      </h2>
      <ul className="mt-2 list-disc space-y-1 ps-5 text-sm text-red-900 dark:text-red-100">
        {errors.map((e, i) => (
          <li key={`${e.path}-${i}`}>
            {e.href ? (
              <a className="underline" href={e.href}>
                {e.message}
              </a>
            ) : (
              e.message
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const BADGE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-navy-800 dark:text-slate-200',
  preparing: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  scheduled: 'bg-navy-100 text-navy-800 dark:bg-navy-700 dark:text-navy-50',
  running: 'bg-teal-100 text-teal-800 dark:bg-teal-800/50 dark:text-teal-100',
  paused: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100',
  cancelled: 'bg-slate-200 text-slate-700 dark:bg-navy-700 dark:text-slate-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-100',
  simulation: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  live: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-100',
};

export function Badge({ kind, children }: { kind: string; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        BADGE[kind] ?? BADGE.draft,
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  icon = 'inbox',
  title,
  children,
  action,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Icon name={icon} className="h-10 w-10 text-slate-400" />
      <p className="font-semibold text-slate-800 dark:text-slate-100">{title}</p>
      {children && <div className="max-w-md text-sm text-slate-600 dark:text-slate-400">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900 dark:text-white">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('card', className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-navy-700">
          {title && <h2 className="font-semibold text-navy-900 dark:text-white">{title}</h2>}
          {actions}
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Phone numbers always render left-to-right, even inside Hebrew/Yiddish text. */
export function Phone({ e164, formatted }: { e164: string; formatted?: string }) {
  return (
    <bdi dir="ltr" className="font-mono text-[0.95em] whitespace-nowrap">
      {formatted ?? e164}
    </bdi>
  );
}

/** Names and user text: direction follows content. */
export function Auto({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <bdi dir="auto" className={className}>
      {children}
    </bdi>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-navy-700">
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold text-navy-900 tabular-nums dark:text-white">{value}</p>
      {hint && <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  );
}
